import {
  AccessTime as ClockIcon,
  CheckCircle as ApprovedIcon,
  CancelRounded as RejectedIcon
} from '@mui/icons-material';
import { Box, Chip, Typography } from '@mui/material';

import {
  ApprovalMode,
  ApproverStatus,
  GateStatus,
  Invoice,
  InvoiceStages
} from 'openapi';

import { getApprovalModeLabel } from 'utils/helpers/approvalsHelpers';

import * as GatesCardStyles from 'styles/components/GatesCardStyle';
import * as GatesCardStyleUtils from 'styles/shared/GatesCardStyleUtils';

interface GatesCardProps {
  currentInvoice: Invoice;
  translate: (key: string, params?: Record<string, string>) => string;
}

const formatDate = (dateString?: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getModeColors = (mode?: ApprovalMode) => {
  switch (mode) {
    case ApprovalMode.SEQUENTIAL:
      return {
        backgroundColor: GatesCardStyleUtils.APPROVAL_MODE_SEQUENTIAL_BG,
        color: GatesCardStyleUtils.APPROVAL_MODE_SEQUENTIAL_COLOR
      };
    case ApprovalMode.PARALLEL:
      return {
        backgroundColor: GatesCardStyleUtils.APPROVAL_MODE_PARALLEL_BG,
        color: GatesCardStyleUtils.APPROVAL_MODE_PARALLEL_COLOR
      };
    case ApprovalMode.AUTO_APPROVE:
      return {
        backgroundColor: GatesCardStyleUtils.APPROVAL_MODE_AUTO_APPROVE_BG,
        color: GatesCardStyleUtils.APPROVAL_MODE_AUTO_APPROVE_COLOR
      };
    default:
      return {
        backgroundColor: GatesCardStyleUtils.APPROVAL_MODE_DEFAULT_BG,
        color: GatesCardStyleUtils.APPROVAL_MODE_DEFAULT_COLOR
      };
  }
};

// Helper function to get border color based on status
const getBorderColor = (isRejected: boolean, isCleared: boolean): string => {
  if (isRejected) return GatesCardStyleUtils.GATES_STATUS_REJECTED;
  if (isCleared) return GatesCardStyleUtils.GATES_STATUS_CLEARED;
  return GatesCardStyleUtils.GATES_BORDER_DEFAULT;
};

// Helper function to get status badge info
const getStatusBadgeInfo = (
  gateStatus: GateStatus,
  translate: (key: string, params?: Record<string, string>) => string
) => {
  if (gateStatus === GateStatus.CLEARED) {
    return {
      label: translate('approvals.cleared'),
      style: GatesCardStyles.clearedStatusStyle
    };
  }
  if (gateStatus === GateStatus.REJECTED) {
    return {
      label: translate('approvals.rejected'),
      style: GatesCardStyles.rejectedStatusStyle
    };
  }
  return {
    label: translate('approvals.pending'),
    style: GatesCardStyles.pendingStatusStyle
  };
};

// Helper function to get approver status text and color
const getApproverStatusInfo = (
  isRejected: boolean,
  isApproved: boolean,
  translate: (key: string, params?: Record<string, string>) => string
) => {
  if (!isApproved && !isRejected) {
    return { text: '', color: '' };
  }
  const text = translate(
    isRejected ? 'approvals.rejected' : 'approvals.approved'
  );
  const color = isRejected
    ? GatesCardStyleUtils.GATES_STATUS_REJECTED
    : GatesCardStyleUtils.GATES_STATUS_CLEARED;
  return { text, color };
};

const getStatusKey = (approvalStatus: ApproverStatus | undefined) => {
  switch (approvalStatus) {
    case ApproverStatus.PENDING_APPROVAL:
      return 'approvals.pendingApproval';
    case ApproverStatus.WAITING:
      return 'approvals.waiting';
    case ApproverStatus.CANCELLED:
      return 'approvals.cancelled';
    default:
      return '';
  }
};

// Helper function to get the appropriate icon for an approver
const getApproverIcon = (isRejected: boolean, isApproved: boolean) => {
  if (isRejected) {
    return <RejectedIcon sx={GatesCardStyles.rejectedIconStyle} />;
  }
  if (isApproved) {
    return <ApprovedIcon sx={GatesCardStyles.approvedIconStyle} />;
  }
  return (
    <ClockIcon
      sx={GatesCardStyles.clockIconStyle(
        GatesCardStyleUtils.GATES_STATUS_PENDING_COLOR
      )}
    />
  );
};

// Helper function to get message for empty gates based on invoice stage
const getMissingGatesInfoMessage = (
  stage?: InvoiceStages,
  translate?: (key: string) => string
): string => {
  if (!translate) return '';

  switch (stage) {
    case InvoiceStages.APPROVED:
      return translate('messages.approvedNoApprovalGates');
    case InvoiceStages.REJECTED:
      return translate('messages.rejectedNoApprovalGates');
    case InvoiceStages.VALIDATED:
      return translate('messages.validatedNoApprovalGates');
    case InvoiceStages.FINALIZED:
      return translate('messages.finalizedNoApprovalGates');
    default:
      return '';
  }
};

// Helper function to get background color for empty gates based on invoice stage
const getEmptyGatesBackgroundColor = (stage?: InvoiceStages): string => {
  switch (stage) {
    case InvoiceStages.APPROVED:
      return '#DCFCE7';
    case InvoiceStages.REJECTED:
      return '#FEE2E2';
    case InvoiceStages.VALIDATED:
      return '#fef9e7';
    case InvoiceStages.FINALIZED:
      return '#DCFCE7';
    default:
      return '#f9fafb';
  }
};

// Helper function to get text color for empty gates based on invoice stage
const getEmptyGatesTextColor = (stage?: InvoiceStages): string => {
  switch (stage) {
    case InvoiceStages.APPROVED:
      return '#166534';
    case InvoiceStages.REJECTED:
      return '#991b1b';
    case InvoiceStages.VALIDATED:
      return '#b45309';
    case InvoiceStages.FINALIZED:
      return '#166534';
    default:
      return '#374151';
  }
};

export const GatesCard = ({ currentInvoice, translate }: GatesCardProps) => {
  const gates = currentInvoice.gates || [];
  const clearedCount = gates.filter(
    (g) => g.gateStatus === GateStatus.CLEARED
  ).length;

  const hasRejectedGate = gates.some(
    (g) => g.gateStatus === GateStatus.REJECTED
  );

  const summaryHeaderStyle = hasRejectedGate
    ? {
        ...GatesCardStyles.gatesSummaryHeaderStyle,
        backgroundColor: GatesCardStyleUtils.GATES_SUMMARY_HEADER_REJECTED_BG,
        color: GatesCardStyleUtils.GATES_SUMMARY_HEADER_REJECTED_COLOR,
        borderColor: GatesCardStyleUtils.GATES_SUMMARY_HEADER_REJECTED_COLOR
      }
    : GatesCardStyles.gatesSummaryHeaderStyle;

  const summaryTypographyStyle = hasRejectedGate
    ? {
        ...GatesCardStyles.summaryTypographyStyle,
        color: GatesCardStyleUtils.GATES_SUMMARY_HEADER_REJECTED_COLOR
      }
    : GatesCardStyles.summaryTypographyStyle;

  return (
    <Box sx={{ mt: 2, pb: 2 }}>
      {gates.length > 0 ? (
        <>
          <Box sx={summaryHeaderStyle}>
            <ClockIcon sx={{ fontSize: '1rem' }} />
            <Typography variant="body1" sx={summaryTypographyStyle}>
              {translate('messages.approvalGatesSummary', {
                clearedCount: String(clearedCount),
                total: String(gates.length)
              })}
            </Typography>
          </Box>

          {gates.map((gate, index) => {
            const isGateCleared = gate.gateStatus === GateStatus.CLEARED;
            const isGateRejected = gate.gateStatus === GateStatus.REJECTED;
            const borderColor = getBorderColor(isGateRejected, isGateCleared);
            const modeColors = getModeColors(gate.approvalMode);
            const statusBadgeInfo = getStatusBadgeInfo(
              gate.gateStatus,
              translate
            );

            const currentApproverName =
              gate.gateApprovers?.[0]?.approver?.name ||
              translate('approvals.unknown');

            return (
              <Box
                key={gate.id || index}
                sx={GatesCardStyles.gateContainerStyle(borderColor)}
              >
                <Box sx={GatesCardStyles.gateHeaderStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="body1"
                      sx={GatesCardStyles.gateNameStyle}
                    >
                      {gate.name || translate('approvals.unnamedGate')}
                    </Typography>
                    {gate.approvalMode && (
                      <Chip
                        label={getApprovalModeLabel(
                          gate.approvalMode,
                          translate
                        )}
                        size="small"
                        sx={GatesCardStyles.modeBadgeStyle(
                          modeColors.backgroundColor,
                          modeColors.color
                        )}
                      />
                    )}
                    {gate.exclusive && (
                      <Chip
                        label={translate('approvals.exclusive')}
                        size="small"
                        sx={GatesCardStyles.exclusiveBadgeStyle}
                      />
                    )}
                  </Box>

                  <Chip
                    label={statusBadgeInfo.label}
                    size="small"
                    sx={statusBadgeInfo.style}
                  />
                </Box>

                {!isGateCleared && !isGateRejected ? (
                  <Box sx={GatesCardStyles.currentApproverContainerStyle}>
                    <Typography
                      variant="body2"
                      sx={GatesCardStyles.currentApproverTextStyle}
                    >
                      {`${translate('approvals.currentApprover')}: ${currentApproverName}`}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={GatesCardStyles.clearedInfoStyle}>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {translate(
                        isGateRejected
                          ? 'approvals.rejected'
                          : 'approvals.cleared'
                      )}
                    </Typography>
                    {gate.resolvedAt && (
                      <Box sx={GatesCardStyles.clearedCircleStyle}></Box>
                    )}
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {gate.resolvedAt ? formatDate(gate.resolvedAt) : ''}
                    </Typography>
                  </Box>
                )}

                {gate.gateApprovers && gate.gateApprovers.length > 0 && (
                  <Box sx={GatesCardStyles.borderBottomDividerStyle} />
                )}

                {gate.gateApprovers && gate.gateApprovers.length > 0 && (
                  <Box sx={GatesCardStyles.approversListStyle}>
                    {gate.gateApprovers.map((approver, approverIndex) => {
                      const isRejected =
                        approver.approvalStatus === ApproverStatus.REJECTED;
                      const isApproved =
                        approver.approvalStatus === ApproverStatus.APPROVED;

                      const statusInfo = getApproverStatusInfo(
                        isRejected,
                        isApproved,
                        translate
                      );
                      const shouldShowStatusInfo = isApproved || isRejected;
                      const respondedColor = isRejected
                        ? GatesCardStyleUtils.GATES_STATUS_REJECTED
                        : GatesCardStyleUtils.GATES_STATUS_CLEARED;

                      return (
                        <Box
                          key={approver.id || approverIndex}
                          display="flex"
                          flexDirection="column"
                        >
                          <Box sx={GatesCardStyles.approverRowStyle}>
                            <Box display="flex" alignItems="center" gap={1}>
                              {getApproverIcon(isRejected, isApproved)}
                              <Typography
                                variant="body2"
                                sx={{ fontSize: '0.875rem' }}
                              >
                                {approver.approver?.name ||
                                  translate('approvals.unknown')}
                              </Typography>
                            </Box>

                            {shouldShowStatusInfo ? (
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    color: statusInfo.color
                                  }}
                                >
                                  {statusInfo.text}
                                </Typography>
                                {approver.respondedAt && (
                                  <Box
                                    sx={{
                                      ...GatesCardStyles.approvedCircleStyle,
                                      backgroundColor: respondedColor
                                    }}
                                  ></Box>
                                )}
                                <Typography
                                  variant="body2"
                                  sx={{ color: respondedColor }}
                                >
                                  {approver.respondedAt
                                    ? formatDate(approver.respondedAt)
                                    : ''}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2">
                                {translate(
                                  getStatusKey(approver.approvalStatus)
                                )}
                              </Typography>
                            )}
                          </Box>

                          {isRejected && approver.responseComment && (
                            <Typography
                              variant="body2"
                              sx={GatesCardStyles.rejectedCommentStyle}
                            >
                              {approver.responseComment}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </>
      ) : (
        <Box
          sx={{
            mt: 2,
            p: 2,
            textAlign: 'left',
            backgroundColor: getEmptyGatesBackgroundColor(currentInvoice.stage),
            borderRadius: 1,
            border: '1px dashed',
            borderColor: getEmptyGatesTextColor(currentInvoice.stage)
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: getEmptyGatesTextColor(currentInvoice.stage) }}
          >
            {getMissingGatesInfoMessage(currentInvoice.stage, translate)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
